import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Brain, Cpu, Sparkles, MessageSquare, Database, Save, Key, Eye, EyeOff, Copy, Check } from 'lucide-react';

import { Switch } from "@/components/ui/switch";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger
} from "@/components/ui/accordion";

export const LLMSettingsSection = () => {
    const [keys, setKeys] = useState({
        deepseek_key: '',
        anthropic_key: '',
        gpt_key: '',
        kimi_key: '',
        qwen_key: '',
        deepseek_enabled: false,
        anthropic_enabled: false,
        gpt_enabled: false,
        kimi_enabled: false,
        qwen_enabled: false
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showKeys, setShowKeys] = useState<{ [key: string]: boolean }>({});
    const { toast } = useToast();

    useEffect(() => {
        fetchKeys();
    }, []);

    const fetchKeys = async () => {
        try {
            const { data, error } = await supabase
                .from('api_llm')
                .select('*')
                .limit(1)
                .single();

            if (error) {
                if (error.code !== 'PGRST116') {
                    console.warn("Erro ao buscar chaves LLM:", error);
                }
                return;
            }
            if (data) {
                setKeys({
                    deepseek_key: data.deepseek_key || '',
                    anthropic_key: data.anthropic_key || '',
                    gpt_key: data.gpt_key || '',
                    kimi_key: data.kimi_key || '',
                    qwen_key: data.qwen_key || '',
                    deepseek_enabled: !!data.deepseek_enabled,
                    anthropic_enabled: !!data.anthropic_enabled,
                    gpt_enabled: !!data.gpt_enabled,
                    kimi_enabled: !!data.kimi_enabled,
                    qwen_enabled: !!data.qwen_enabled,
                });
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);

        try {
            const { data: existing } = await supabase.from('api_llm').select('id').limit(1).single();

            let error;
            const updatePayload = {
                deepseek_key: keys.deepseek_key,
                anthropic_key: keys.anthropic_key,
                gpt_key: keys.gpt_key,
                kimi_key: keys.kimi_key,
                qwen_key: keys.qwen_key,
                updated_at: new Date().toISOString()
            };

            if (existing) {
                const result = await supabase
                    .from('api_llm')
                    .update(updatePayload)
                    .eq('id', existing.id);
                error = result.error;
            } else {
                const result = await supabase
                    .from('api_llm')
                    .insert(updatePayload);
                error = result.error;
            }

            if (error) throw error;

            toast({
                title: "Configurações salvas",
                description: "As chaves de API foram atualizadas com sucesso."
            });
        } catch (error) {
            console.error("Erro ao salvar chaves:", error);
            toast({
                title: "Erro ao salvar",
                description: "Não foi possível atualizar as chaves de API.",
                variant: 'destructive',
            });
        } finally {
            setSaving(false);
        }
    };

    const toggleApiStatus = async (key: string, enabledKey: string, currentValue: boolean) => {
        // Bloquear ativação se não houver chave salva
        if (!currentValue && !keys[key as keyof typeof keys]) {
            toast({
                title: "Chave ausente",
                description: "Por favor, insira e salve a chave de API antes de ativar o serviço.",
                variant: 'destructive'
            });
            return;
        }

        const newValue = !currentValue;

        // Update otimista no front
        setKeys(prev => ({ ...prev, [enabledKey]: newValue }));

        try {
            const { data: existing } = await supabase.from('api_llm').select('id').limit(1).single();
            if (!existing) throw new Error("Registro base não encontrado. Salve as chaves primeiro.");

            const { error } = await supabase
                .from('api_llm')
                .update({ [enabledKey]: newValue })
                .eq('id', existing.id);

            if (error) throw error;

            toast({
                title: newValue ? "API Ativada" : "API Desativada",
                description: `O status da API foi atualizado com sucesso.`
            });
        } catch (error) {
            console.error("Erro ao alternar status da API:", error);
            // Reverter em caso de erro
            setKeys(prev => ({ ...prev, [enabledKey]: currentValue }));
            toast({
                title: "Erro",
                description: "Não foi possível alterar o status da API.",
                variant: 'destructive'
            });
        }
    };

    const toggleVisibility = (id: string, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setShowKeys(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const handleCopy = async (id: string, value: string, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (!value) {
            toast({
                title: "Campo vazio",
                description: "Não há chave para copiar.",
                variant: 'destructive'
            });
            return;
        }

        try {
            await navigator.clipboard.writeText(value);
            setShowKeys(prev => ({ ...prev, [`copied_${id}`]: true }));

            toast({
                description: "Chave copiada para a área de transferência!",
                duration: 2000
            });

            // Reset icon after 2 seconds
            setTimeout(() => {
                setShowKeys(prev => ({ ...prev, [`copied_${id}`]: false }));
            }, 2000);
        } catch (err) {
            console.error('Erro ao copiar:', err);
            toast({
                title: "Erro ao copiar",
                description: "Não foi possível copiar para a área de transferência.",
                variant: 'destructive'
            });
        }
    };

    const LLMItem = ({ id, label, description, icon: Icon, value, onChangeKey, enabledKey }: any) => {
        const isEnabled = keys[enabledKey as keyof typeof keys] as boolean;
        const hasKey = !!keys[onChangeKey as keyof typeof keys];

        return (
            <AccordionItem value={id} className={`border rounded-lg mb-2 overflow-hidden bg-white shadow-sm transition-all duration-200 ${isEnabled ? 'border-emerald-200 ring-1 ring-emerald-50/50' : 'border-slate-200 hover:border-slate-300'}`}>
                <div className="flex items-center w-full pr-4">
                    <AccordionTrigger className="px-4 hover:bg-slate-50/80 hover:no-underline transition-colors py-4 flex-1 overflow-hidden">
                        <div className="flex items-center gap-4 text-left overflow-hidden">
                            <div className={`p-2.5 rounded-xl transition-all duration-300 ${isEnabled ? 'bg-emerald-100 text-emerald-600 shadow-sm shadow-emerald-100' : 'bg-slate-100 text-slate-500'}`}>
                                <Icon className="w-5 h-5" />
                            </div>
                            <div className="flex flex-col min-w-0">
                                <span className="font-bold text-slate-900 text-base flex items-center gap-2">
                                    {label}
                                    {isEnabled && (
                                        <span className="flex h-2 w-2 rounded-full bg-emerald-500">
                                            <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-emerald-400 opacity-75"></span>
                                        </span>
                                    )}
                                </span>
                                <span className="text-xs text-slate-500 font-medium truncate">{description}</span>
                            </div>
                        </div>
                    </AccordionTrigger>
                    <div className="flex items-center gap-4 border-l border-slate-100 pl-4 py-2" onClick={(e) => e.stopPropagation()}>
                        <div className="flex flex-col items-end mr-1">
                            <span className={`text-[10px] font-bold uppercase tracking-widest ${isEnabled ? 'text-emerald-600' : 'text-slate-400'}`}>
                                {isEnabled ? 'Ativo' : 'Inativo'}
                            </span>
                        </div>
                        <Switch
                            checked={isEnabled}
                            onCheckedChange={() => toggleApiStatus(onChangeKey, enabledKey, isEnabled)}
                            disabled={!hasKey && !isEnabled}
                            className="data-[state=checked]:bg-emerald-600"
                        />
                    </div>
                </div>
                <AccordionContent className="px-4 pb-5 pt-1 bg-slate-50/30">
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center justify-between px-0.5">
                            <Label htmlFor={id} className="text-xs font-semibold text-slate-700">
                                Chave de API {label}
                            </Label>
                            {hasKey && (
                                <span className="text-[10px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full border border-emerald-100 font-bold uppercase">
                                    Configurada
                                </span>
                            )}
                        </div>
                        <div className="relative group">
                            <Input
                                id={id}
                                type={showKeys[id] ? "text" : "password"}
                                value={value}
                                onChange={(e) => setKeys(prev => ({ ...prev, [onChangeKey]: e.target.value }))}
                                placeholder={`Insira a chave ${label} e clique em Salvar...`}
                                className="pr-24 h-11 bg-white border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/20 transition-all rounded-lg"
                                autoComplete="off"
                            />
                            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 z-10 bg-white/50 backdrop-blur-sm pr-1">
                                <button
                                    type="button"
                                    onClick={(e) => handleCopy(id, value, e)}
                                    className="text-slate-400 hover:text-emerald-600 transition-colors p-1.5 rounded-md hover:bg-emerald-50"
                                    title="Copiar chave"
                                >
                                    {showKeys[`copied_${id}`] ? (
                                        <Check className="w-4 h-4 text-emerald-600" />
                                    ) : (
                                        <Copy className="w-4 h-4" />
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={(e) => toggleVisibility(id, e)}
                                    className="text-slate-400 hover:text-emerald-600 transition-colors p-1.5 rounded-md hover:bg-emerald-50"
                                    title={showKeys[id] ? "Ocultar chave" : "Mostrar chave"}
                                >
                                    {showKeys[id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                        {!hasKey && (
                            <div className="flex items-center gap-1.5 px-0.5">
                                <div className="w-1 h-1 rounded-full bg-red-400" />
                                <p className="text-[10px] text-red-500 font-medium italic">
                                    Você precisa salvar a chave antes de poder ativar esta API.
                                </p>
                            </div>
                        )}
                    </div>
                </AccordionContent>
            </AccordionItem>
        );
    };

    if (loading) {
        return (
            <div className="p-12 text-center">
                <div className="w-8 h-8 border-3 border-emerald-100 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4" />
                <p className="text-slate-500 font-medium">Carregando motores de IA...</p>
            </div>
        );
    }

    return (
        <Card className="w-full bg-slate-50/50 border-none shadow-none">
            <CardHeader className="px-0 pt-0 pb-6">
                <div className="flex items-center gap-4">
                    <div className="p-3.5 bg-emerald-600 rounded-2xl text-white shadow-lg shadow-emerald-200/50 rotate-3 group-hover:rotate-0 transition-transform duration-300">
                        <Brain className="w-6 h-6" />
                    </div>
                    <div>
                        <CardTitle className="text-2xl text-slate-800 font-black tracking-tight">Gerenciamento de IA</CardTitle>
                        <CardDescription className="text-slate-500 font-medium max-w-lg">
                            Configure suas chaves de API e ative os modelos conforme necessário. O estado de ativação é salvo instantaneamente.
                        </CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="px-0">
                <form onSubmit={handleSave} className="space-y-8">
                    <Accordion type="single" collapsible className="w-full space-y-3">
                        <LLMItem
                            id="deepseek"
                            label="DeepSeek"
                            description="DeepSeek R1/V3 (Eficiência & Performance)"
                            icon={Brain}
                            value={keys.deepseek_key}
                            onChangeKey="deepseek_key"
                            enabledKey="deepseek_enabled"
                        />

                        <LLMItem
                            id="anthropic"
                            label="Anthropic"
                            description="Claude 3.5 Sonnet / Haiku (Raciocínio Refinado)"
                            icon={Sparkles}
                            value={keys.anthropic_key}
                            onChangeKey="anthropic_key"
                            enabledKey="anthropic_enabled"
                        />

                        <LLMItem
                            id="gpt"
                            label="OpenAI GPT"
                            description="GPT-4o / o1 (Padrão da Indústria)"
                            icon={Cpu}
                            value={keys.gpt_key}
                            onChangeKey="gpt_key"
                            enabledKey="gpt_enabled"
                        />

                        <LLMItem
                            id="kimi"
                            label="Kimi k2.5"
                            description="Moonshot AI (Janela de Contexto de 128k)"
                            icon={MessageSquare}
                            value={keys.kimi_key}
                            onChangeKey="kimi_key"
                            enabledKey="kimi_enabled"
                        />

                        <LLMItem
                            id="qwen"
                            label="Qwen"
                            description="Alibaba Qwen 2.5 (Poder de Codificação)"
                            icon={Database}
                            value={keys.qwen_key}
                            onChangeKey="qwen_key"
                            enabledKey="qwen_enabled"
                        />
                    </Accordion>

                    <div className="flex justify-end pt-6">
                        <Button
                            type="submit"
                            disabled={saving}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-14 px-12 rounded-xl shadow-lg shadow-emerald-200/40 hover:shadow-emerald-500/20 hover:-translate-y-0.5 active:scale-95 transition-all duration-200 flex items-center gap-3 group min-w-[240px]"
                        >
                            {saving ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                <Save className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
                            )}
                            <span className="text-base tracking-tight">
                                {saving ? "Salvando Alterações..." : "Salvar chaves de API LLM"}
                            </span>
                        </Button>
                    </div>
                </form>
            </CardContent>
        </Card>
    );
};
